import React, { useState, useEffect } from 'react';
import { pendientesAPI, obrasAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, CheckSquare, Calendar } from 'lucide-react';
import { formatDate, getPrioridadColor, getTodayISO } from '../lib/utils';

const PendientesPage = () => {
  const [pendientes, setPendientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPendiente, setSelectedPendiente] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    obra_id: '',
    prioridad: 'media',
    fecha_limite: '',
    completado: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [pendientesRes, obrasRes] = await Promise.all([
        pendientesAPI.list(),
        obrasAPI.list(),
      ]);
      setPendientes(pendientesRes.data);
      setObras(obrasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedPendiente) {
        await pendientesAPI.update(selectedPendiente.id, formData);
        toast.success('Tarea actualizada');
      } else {
        await pendientesAPI.create(formData);
        toast.success('Tarea creada');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar tarea');
    }
  };

  const handleToggleComplete = async (pendiente) => {
    try {
      if (pendiente.completado) {
        await pendientesAPI.update(pendiente.id, { ...pendiente, completado: false });
      } else {
        await pendientesAPI.completar(pendiente.id);
      }
      loadData();
    } catch (error) {
      toast.error('Error al actualizar tarea');
    }
  };

  const handleEdit = (pendiente) => {
    setSelectedPendiente(pendiente);
    setFormData({
      titulo: pendiente.titulo || '',
      descripcion: pendiente.descripcion || '',
      obra_id: pendiente.obra_id || '',
      prioridad: pendiente.prioridad || 'media',
      fecha_limite: pendiente.fecha_limite || '',
      completado: pendiente.completado || false,
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await pendientesAPI.delete(selectedPendiente.id);
      toast.success('Tarea eliminada');
      setDeleteDialogOpen(false);
      setSelectedPendiente(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar tarea');
    }
  };

  const resetForm = () => {
    setSelectedPendiente(null);
    setFormData({
      titulo: '',
      descripcion: '',
      obra_id: '',
      prioridad: 'media',
      fecha_limite: '',
      completado: false,
    });
  };

  const getObraName = (obraId) => {
    if (!obraId) return null;
    const obra = obras.find((o) => o.id === obraId);
    return obra ? obra.titulo : null;
  };

  const translatePrioridad = (prioridad) => {
    const map = { baja: 'Baja', media: 'Media', alta: 'Alta' };
    return map[prioridad] || prioridad;
  };

  const filteredPendientes = pendientes.filter(p => showCompleted || !p.completado);
  
  // Sort by: not completed first, then by priority (alta > media > baja), then by date
  const sortedPendientes = [...filteredPendientes].sort((a, b) => {
    if (a.completado !== b.completado) return a.completado ? 1 : -1;
    const prioridadOrder = { alta: 0, media: 1, baja: 2 };
    if (prioridadOrder[a.prioridad] !== prioridadOrder[b.prioridad]) {
      return prioridadOrder[a.prioridad] - prioridadOrder[b.prioridad];
    }
    return new Date(a.fecha_limite || '9999') - new Date(b.fecha_limite || '9999');
  });

  const pendientesCount = pendientes.filter(p => !p.completado).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pendientes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Pendientes</h1>
          <p className="text-slate-500 text-sm mt-1">{pendientesCount} tareas pendientes</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <Checkbox
              checked={showCompleted}
              onCheckedChange={setShowCompleted}
              data-testid="show-completed-checkbox"
            />
            Mostrar completadas
          </label>
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="new-pendiente-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Tarea
          </Button>
        </div>
      </div>

      {/* Tasks List */}
      {sortedPendientes.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <CheckSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">
              {showCompleted ? 'No hay tareas' : '¡Todo al día! No hay tareas pendientes'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {sortedPendientes.map((pendiente) => {
            const isOverdue = !pendiente.completado && pendiente.fecha_limite && 
              new Date(pendiente.fecha_limite) < new Date(getTodayISO());
            
            return (
              <Card
                key={pendiente.id}
                className={`border-slate-200 transition-all ${
                  pendiente.completado ? 'opacity-60' : ''
                } ${pendiente.prioridad === 'alta' && !pendiente.completado ? 'border-l-4 border-l-red-500' : ''}`}
                data-testid={`pendiente-card-${pendiente.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={pendiente.completado}
                      onCheckedChange={() => handleToggleComplete(pendiente)}
                      className="mt-1"
                      data-testid={`toggle-pendiente-${pendiente.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <h3 className={`font-medium ${pendiente.completado ? 'line-through text-slate-500' : 'text-slate-900'}`}>
                            {pendiente.titulo}
                          </h3>
                          {pendiente.descripcion && (
                            <p className="text-sm text-slate-500 mt-1">{pendiente.descripcion}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${getPrioridadColor(pendiente.prioridad)}`}>
                              {translatePrioridad(pendiente.prioridad)}
                            </span>
                            {getObraName(pendiente.obra_id) && (
                              <span className="text-xs text-slate-500">
                                {getObraName(pendiente.obra_id)}
                              </span>
                            )}
                            {pendiente.fecha_limite && (
                              <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                                <Calendar className="w-3 h-3" />
                                {formatDate(pendiente.fecha_limite)}
                                {isOverdue && ' (Vencida)'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(pendiente)}
                            data-testid={`edit-pendiente-${pendiente.id}`}
                          >
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => { setSelectedPendiente(pendiente); setDeleteDialogOpen(true); }}
                            data-testid={`delete-pendiente-${pendiente.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-slate-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="pendiente-dialog">
          <DialogHeader>
            <DialogTitle>{selectedPendiente ? 'Editar Tarea' : 'Nueva Tarea'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="¿Qué hay que hacer?"
                required
                data-testid="pendiente-titulo-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Detalles adicionales..."
                rows={2}
                data-testid="pendiente-descripcion-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select
                  value={formData.prioridad}
                  onValueChange={(value) => setFormData({ ...formData, prioridad: value })}
                >
                  <SelectTrigger data-testid="pendiente-prioridad-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baja">Baja</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha Límite</Label>
                <Input
                  type="date"
                  value={formData.fecha_limite}
                  onChange={(e) => setFormData({ ...formData, fecha_limite: e.target.value })}
                  data-testid="pendiente-fecha-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Obra (opcional)</Label>
              <Select
                value={formData.obra_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, obra_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="pendiente-obra-select">
                  <SelectValue placeholder="Vincular a obra" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin obra</SelectItem>
                  {obras.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.numero} - {o.titulo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-pendiente-btn">
                {selectedPendiente ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tarea?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará la tarea "{selectedPendiente?.titulo}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-pendiente-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PendientesPage;
